import { io, type Socket } from 'socket.io-client'
import { getEnv } from '@utils/env'
import { isStandalonePatientFormsPath } from '@utils/standalonePatientForms.util'
import { getAccessToken } from '@services/auth/accessTokenStore'
import { logger } from '@utils/safeLogger'

function shouldSkipStaffRealtimeSockets(): boolean {
  return (
    typeof window !== 'undefined' &&
    isStandalonePatientFormsPath(window.location.pathname)
  )
}

const FORMS_DEVICE_SESSION_KEY = 'forms_device_session_id'

function getOrCreateDeviceSessionId(): string {
  let id = localStorage.getItem(FORMS_DEVICE_SESSION_KEY)
  if (!id) {
    id = `device_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
    localStorage.setItem(FORMS_DEVICE_SESSION_KEY, id)
  }
  return id
}

function getFormsSocketAuth(): {
  token: string
  dbName: string
  clinicId: string
  deviceSessionId: string
} | null {
  const token = getAccessToken()
  const dbName = localStorage.getItem('db_name')
  const clinicId = localStorage.getItem('clinic_id')
  if (!token || !dbName || !clinicId) return null
  return {
    token,
    dbName,
    clinicId,
    deviceSessionId: getOrCreateDeviceSessionId(),
  }
}

const BUSINESS_LAYER_FORMS = getEnv('REACT_APP_BUSINESS_LAYER_FORMS')

let formsSocket: Socket | null = null
let formsSocketAuthKey: string | null = null

export function disconnectFormsSocket(): void {
  if (formsSocket) {
    formsSocket.removeAllListeners()
    formsSocket.disconnect()
    formsSocket = null
  }
  formsSocketAuthKey = null
}

function connectFormsSocket(): void {
  if (shouldSkipStaffRealtimeSockets()) return
  const auth = getFormsSocketAuth()
  if (!auth) {
    disconnectFormsSocket()
    return
  }
  const authKey = `${auth.dbName}|${auth.clinicId}|${auth.deviceSessionId}`
  // Reauthorize on clinic/tenant switch — do not keep a stale room subscription.
  if (formsSocket?.connected && formsSocketAuthKey === authKey) return
  if (formsSocket) {
    formsSocket.removeAllListeners()
    formsSocket.disconnect()
    formsSocket = null
  }
  formsSocketAuthKey = authKey
  formsSocket = io(BUSINESS_LAYER_FORMS, {
    path: '/api/v1/forms/socket.io/',
    transports: ['websocket'],
    reconnection: true,
    auth: {
      token: auth.token,
      dbName: auth.dbName,
      clinicId: auth.clinicId,
      deviceSessionId: auth.deviceSessionId,
    },
  })
  formsSocket.on('connect', () => {
    logger.info('Forms websocket connected:', formsSocket?.id)
  })
  formsSocket.on('disconnect', (reason) => {
    logger.info('Forms websocket disconnected', reason)
  })
  formsSocket.on('connect_error', (err) => {
    logger.warn('Forms websocket connect_error', err.message)
  })
}

export function getFormsSocket(): Socket | null {
  if (typeof window === 'undefined') return null
  if (shouldSkipStaffRealtimeSockets()) return null
  if (!formsSocket) connectFormsSocket()
  return formsSocket
}

const BUSINESS_LAYER_NOTIFICATIONS = getEnv(
  'REACT_APP_BUSINESS_LAYER_NOTIFICATIONS',
)

const NOTIFICATIONS_DEVICE_SESSION_KEY = 'notifications_device_session_id'

function getOrCreateNotificationsDeviceSessionId(): string {
  let id = localStorage.getItem(NOTIFICATIONS_DEVICE_SESSION_KEY)
  if (!id) {
    id = `device_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
    localStorage.setItem(NOTIFICATIONS_DEVICE_SESSION_KEY, id)
  }
  return id
}

function getNotificationsSocketAuth(): {
  token: string
  dbName: string
  clinicId: string
  deviceSessionId: string
} | null {
  const token = getAccessToken()
  const dbName = localStorage.getItem('db_name')
  const clinicId = localStorage.getItem('clinic_id')
  if (!token || !dbName || !clinicId) return null
  return {
    token,
    dbName,
    clinicId,
    deviceSessionId: getOrCreateNotificationsDeviceSessionId(),
  }
}

let notificationsSocket: Socket | null = null

function connectNotificationsSocket(): void {
  if (shouldSkipStaffRealtimeSockets()) return
  const auth = getNotificationsSocketAuth()
  if (!auth) return
  if (notificationsSocket?.connected) return
  if (notificationsSocket) {
    notificationsSocket.removeAllListeners()
    notificationsSocket.disconnect()
  }
  notificationsSocket = io(BUSINESS_LAYER_NOTIFICATIONS, {
    path: '/api/v1/notifications/socket.io/',
    transports: ['websocket'],
    reconnection: true,
    auth: {
      token: auth.token,
      dbName: auth.dbName,
      clinicId: auth.clinicId,
      deviceSessionId: auth.deviceSessionId,
    },
  })
  notificationsSocket.on('connect', () => {
    logger.info('Notifications websocket connected:', notificationsSocket?.id)
  })
  notificationsSocket.on('disconnect', (reason) => {
    logger.info('Notifications websocket disconnected', reason)
  })
  notificationsSocket.on('connect_error', (err) => {
    logger.warn('Notifications websocket connect_error', err.message)
  })
}

export function getNotificationsSocket(): Socket | null {
  if (typeof window === 'undefined') return null
  if (shouldSkipStaffRealtimeSockets()) return null
  if (!notificationsSocket) connectNotificationsSocket()
  return notificationsSocket
}

export function disconnectNotificationsSocket(): void {
  if (notificationsSocket) {
    notificationsSocket.removeAllListeners()
    notificationsSocket.disconnect()
    notificationsSocket = null
  }
}

const EXTERNAL_LAYER_CALLS = getEnv(
  'REACT_APP_EXTERNAL_LAYER_CALLS',
  'http://localhost:5000',
)

const callSocket = io(EXTERNAL_LAYER_CALLS, {
  path: '/api/v1/external/calls/socket.io/',
  transports: ['websocket'],
  reconnection: true,
  autoConnect: false,
  auth: {
    token: getAccessToken() ?? undefined,
    clinicId: localStorage.getItem('clinic_id') ?? undefined,
  },
})

callSocket.on('connect', () => {
  logger.info('Calls websocket connected:', callSocket.id)
})

callSocket.on('disconnect', () => {
  logger.info('Calls websocket disconnected')
})

/**
 * Connect or reconnect the calls socket with token from getAccessToken().
 * Only connects when authenticated and a clinic is selected (server requires clinicId).
 */
function reconnectCallsSocket(): void {
  if (shouldSkipStaffRealtimeSockets()) {
    if (callSocket.connected) {
      callSocket.disconnect()
    }
    return
  }
  const token = getAccessToken()
  const clinicId = localStorage.getItem('clinic_id')
  if (!token || !clinicId) {
    if (callSocket.connected) {
      callSocket.disconnect()
    }
    return
  }
  callSocket.auth = { token, clinicId }
  if (callSocket.connected) {
    callSocket.disconnect()
  }
  callSocket.connect()
}

function disconnectCallsSocket(): void {
  callSocket.disconnect()
}

const EXTERNAL_LAYER_CHAT = getEnv(
  'REACT_APP_EXTERNAL_LAYER_CHAT',
  'http://localhost:5001',
)

const chatSocket = io(EXTERNAL_LAYER_CHAT, {
  path: '/api/v1/external/chat/socket.io/',
  transports: ['websocket'],
  reconnection: true,
  autoConnect: false,
  auth: {
    token: getAccessToken() ?? undefined,
    clinicId: localStorage.getItem('clinic_id') ?? undefined,
    dbName: localStorage.getItem('db_name') ?? undefined,
  },
})

chatSocket.on('connect', () => {
  logger.info('Chat websocket connected:', chatSocket.id)
})

chatSocket.on('disconnect', () => {
  logger.info('Chat websocket disconnected')
})

const EXTERNAL_LAYER_MARKETING = getEnv(
  'REACT_APP_EXTERNAL_LAYER_MARKETING',
  'http://localhost:5003',
)

const campaignSocket = io(EXTERNAL_LAYER_MARKETING, {
  path: '/api/v1/external/campaign/socket.io/',
  transports: ['websocket'],
  reconnection: true,
  autoConnect: false,
  auth: {
    token: getAccessToken() ?? undefined,
    clinicId: localStorage.getItem('clinic_id') ?? undefined,
    dbName: localStorage.getItem('db_name') ?? undefined,
  },
})

campaignSocket.on('connect', () => {
  logger.info('Campaign websocket connected:', campaignSocket.id)
})

campaignSocket.on('disconnect', () => {
  logger.info('Campaign websocket disconnected')
})

const EXTERNAL_LAYER_SINCH = getEnv('REACT_APP_EXTERNAL_LAYER_SINCH')

const faxSocket = io(EXTERNAL_LAYER_SINCH, {
  path: '/api/v1/external/sinch/socket.io/',
  transports: ['websocket'],
  reconnection: true,
  autoConnect: false,
  auth: {
    token: getAccessToken() ?? undefined,
    clinicId: localStorage.getItem('clinic_id') ?? undefined,
  },
})

faxSocket.on('connect', () => {
  logger.info('Fax websocket connected:', faxSocket.id)
})

faxSocket.on('disconnect', () => {
  logger.info('Fax websocket disconnected')
})

function reconnectFaxSocket(): void {
  if (shouldSkipStaffRealtimeSockets()) {
    if (faxSocket.connected) {
      faxSocket.disconnect()
    }
    return
  }
  const token = getAccessToken()
  const clinicId = localStorage.getItem('clinic_id')
  const dbName = localStorage.getItem('db_name')
  if (!token || !dbName || !clinicId) {
    if (faxSocket.connected) {
      faxSocket.disconnect()
    }
    return
  }
  faxSocket.auth = { token, clinicId }
  if (faxSocket.connected) {
    faxSocket.disconnect()
  }
  faxSocket.connect()
}

function disconnectFaxSocket(): void {
  faxSocket.disconnect()
}

const BUSINESS_LAYER_CHAT = getEnv(
  'REACT_APP_BUSINESS_LAYER_CHAT',
  'http://localhost:3011',
)

const internalChatSocket = io(BUSINESS_LAYER_CHAT, {
  path: '/api/v1/chat/socket.io/',
  transports: ['websocket'],
  reconnection: true,
  autoConnect: false,
  auth: {
    token: getAccessToken() ?? undefined,
    clinicId: localStorage.getItem('clinic_id') ?? undefined,
    dbName: localStorage.getItem('db_name') ?? undefined,
  },
})

internalChatSocket.on('connect', () => {
  logger.info('Internal chat websocket connected:', internalChatSocket.id)
})

internalChatSocket.on('disconnect', () => {
  logger.info('Internal chat websocket disconnected')
})

function disconnectMessagingSockets(): void {
  chatSocket.disconnect()
  campaignSocket.disconnect()
  internalChatSocket.disconnect()
}

/**
 * Connect chat, campaign, and internal-chat sockets after login when tenant + clinic context exists.
 * With autoConnect: false, importing this module does not open sockets on the login screen.
 */
function reconnectMessagingSockets(): void {
  if (shouldSkipStaffRealtimeSockets()) {
    disconnectMessagingSockets()
    return
  }
  const token = getAccessToken()
  const clinicId = localStorage.getItem('clinic_id')
  const dbName = localStorage.getItem('db_name')
  if (!token || !dbName || !clinicId) {
    disconnectMessagingSockets()
    return
  }
  const auth = { token, clinicId, dbName }
  chatSocket.auth = auth
  campaignSocket.auth = auth
  internalChatSocket.auth = auth
  for (const socket of [chatSocket, campaignSocket, internalChatSocket]) {
    if (socket.connected) {
      socket.disconnect()
    }
    socket.connect()
  }
}

export function disconnectAllStaffRealtimeSockets(): void {
  disconnectFormsSocket()
  disconnectNotificationsSocket()
  disconnectCallsSocket()
  disconnectMessagingSockets()
  disconnectFaxSocket()
}

export {
  callSocket,
  reconnectCallsSocket,
  disconnectCallsSocket,
  chatSocket,
  campaignSocket,
  faxSocket,
  reconnectFaxSocket,
  disconnectFaxSocket,
  connectFormsSocket,
  reconnectMessagingSockets,
  disconnectMessagingSockets,
  internalChatSocket,
  connectNotificationsSocket,
}
